if (!customElements.get('fuse-research')) {
  const PHONE = '(max-width: 700px)';
  const FORMULA_PREFIX = 'research-formula-';
  // Reader-visible text: skips screen-reader-only and aria-hidden parts.
  const visibleText = (node) => {
    const copy = node.cloneNode(true);
    copy.querySelectorAll('.visually-hidden, [aria-hidden="true"]').forEach((hidden) => hidden.remove());
    return copy.textContent.replace(/\s+/g, ' ').trim();
  };
  customElements.define('fuse-research', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs = [...this.querySelectorAll('[data-formula-tab]')];
      this.panels = [...this.querySelectorAll('[data-formula-panel]')];
      this.sources = [...this.querySelectorAll('[data-source]')];
      this.index = this.sources.map((source) => this.indexSource(source));
      this.numbers = new Set(this.index.map((entry) => entry.number));
      this.search = this.querySelector('[data-source-search]');
      this.clearButton = this.querySelector('[data-source-clear]');
      this.countNode = this.querySelector('[data-source-count]');
      this.sourceFilters = [...this.querySelectorAll('[data-source-filter]')];
      this.sourceFormula = 'all';
      this.shortcutFormula = false;
      this.jumpLinks = [...this.querySelectorAll('.fr-jump a')];
      this.readingSections = this.jumpLinks.map((link) => this.querySelector(link.hash));
      this.querySelector('[data-formula-tabs]').setAttribute('role', 'tablist');
      this.search.setAttribute('enterkeyhint', 'search');
      this.labelCitations();
      this.tabs.forEach((tab) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.hash.slice(1));
        tab.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.selectFormula(tab.dataset.formulaTab, true);
          // A tap on a phone puts the strip and panel in one frame.
          if (event.detail > 0) this.revealPanel(tab.dataset.formulaTab);
        }, { signal });
        tab.addEventListener('keydown', (event) => {
          if (event.key === ' ') {
            event.preventDefault();
            this.selectFormula(tab.dataset.formulaTab, true);
            return;
          }
          const current = this.tabs.indexOf(tab);
          const count = this.tabs.length;
          // A 3 x 2 strip (tablet, phone): Up/Down move between rows.
          const row = this.tabs.filter((item) => item.offsetTop === this.tabs[0].offsetTop).length;
          const columns = row < count ? row : 0;
          let next;
          if (event.key === 'ArrowRight') next = (current + 1) % count;
          if (event.key === 'ArrowLeft') next = (current - 1 + count) % count;
          if (columns && event.key === 'ArrowDown') next = (current + columns) % count;
          if (columns && event.key === 'ArrowUp') next = (current - columns + count) % count;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = count - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.selectFormula(this.tabs[next].dataset.formulaTab, true);
          this.tabs[next].focus({ preventScroll: true });
        }, { signal });
      });
      this.panels.forEach((panel) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `research-tab-${panel.dataset.formulaPanel}`);
      });
      this.search.addEventListener('input', () => {
        this.shortcutFormula = false;
        this.filterSources(true);
      }, { signal });
      this.search.addEventListener('keydown', (event) => {
        // Enter dismisses the soft keyboard so the results stay visible.
        if (event.key === 'Enter' && matchMedia('(pointer: coarse)').matches) this.search.blur();
      }, { signal });
      this.sourceFilters.forEach((button) => {
        button.addEventListener('click', () => {
          this.sourceFormula = button.dataset.sourceFilter;
          this.shortcutFormula = false;
          this.filterSources();
        }, { signal });
      });
      this.clearButton.addEventListener('click', () => {
        const hadText = Boolean(this.search.value);
        this.resetSourceFilter();
        // Clear hides itself: keep focus meaningful; raise the keyboard only if there was text.
        (hadText ? this.search : this.sourceFilters.find((button) => button.dataset.sourceFilter === 'all'))?.focus();
      }, { signal });
      this.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const formulaLink = event.target.closest('[data-formula-sources]');
        if (formulaLink) {
          this.search.value = '';
          this.sourceFormula = formulaLink.dataset.formulaSources;
          this.shortcutFormula = true;
          this.filterSources();
          // Native #research-sources navigation focuses the section: step onto its heading.
          setTimeout(() => this.focusLibrary(), 0);
          // On phones the filters fill the first screen: bring the results up.
          if (matchMedia(PHONE).matches) requestAnimationFrame(() => this.scrollToElement(this.querySelector('[data-source-tools]'), 16));
          return;
        }
        const link = event.target.closest('[data-source-link]');
        if (!link) return;
        const target = this.querySelector(link.hash);
        if (!target) return;
        this.resetSourceFilter();
        // Keep native anchor navigation and history.
        target.focus({ preventScroll: true });
        this.markLanded(target);
      }, { signal });
      // Remember how a same-document navigation started (Back/Forward keep native scroll restoration).
      window.navigation?.addEventListener('navigate', (event) => { this.navType = event.navigationType; }, { signal });
      document.addEventListener('click', (event) => {
        if (event.target.closest?.('a[href^="#"]')) this.linkClickAt = performance.now();
      }, { capture: true, signal });
      window.addEventListener('hashchange', () => this.readHash(), { signal });
      window.addEventListener('scroll', () => this.queueReadingPosition(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueReadingPosition(), { signal });
      this.updateHeaderReserve();
      // Reload and Back/Forward restore the visitor's scroll: only a fresh arrival lands on the hash.
      this.landing = !['reload', 'back_forward'].includes(performance.getEntriesByType?.('navigation')?.[0]?.type);
      this.selectFormula(this.dataset.defaultFormula || this.tabs[0].dataset.formulaTab);
      // Lift the pre-JS gate only once the other panels are hidden, so the page never lays out all six at once.
      this.setAttribute('data-enhanced', '');
      this.querySelector('[data-source-tools]').hidden = false;
      this.filterSources();
      this.readHash(true);
      const header = document.querySelector('.site-header');
      if (header) {
        this.headerObserver?.disconnect();
        this.headerObserver = new ResizeObserver(() => {
          this.updateHeaderReserve();
          this.queueReadingPosition();
        });
        this.headerObserver.observe(header);
        if (header.parentElement) this.headerObserver.observe(header.parentElement);
      }
      this.queueReadingPosition();
    }

    selectFormula(name, updateHash = false) {
      if (!this.tabs.some((tab) => tab.dataset.formulaTab === name)) return false;
      this.clearLanded();
      this.tabs.forEach((tab) => {
        const active = tab.dataset.formulaTab === name;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.formulaPanel !== name; });
      if (updateHash) {
        history.replaceState(null, '', `#${FORMULA_PREFIX}${name}`);
        // A "Sources for X" filter goes stale once another formula is chosen.
        if (this.shortcutFormula && this.sourceFormula !== name) {
          this.sourceFormula = 'all';
          this.shortcutFormula = false;
          this.filterSources();
        }
      }
      this.queueReadingPosition();
      return true;
    }

    hashId() {
      try {
        return decodeURIComponent(location.hash.slice(1));
      } catch {
        return '';
      }
    }

    linkDriven() {
      if (window.navigation) return this.navType !== 'traverse';
      return performance.now() - (this.linkClickAt ?? -Infinity) < 1000;
    }

    readHash(initial = false) {
      const id = this.hashId();
      if (!id) return;
      const isFormula = id.startsWith(FORMULA_PREFIX);
      if (isFormula) {
        if (!this.selectFormula(id.slice(FORMULA_PREFIX.length))) return;
      } else if (id.startsWith('source-')) {
        this.resetSourceFilter();
        const row = document.getElementById(id);
        if (row && this.contains(row)) this.markLanded(row);
      }
      if (initial) {
        if (this.landing && this.scrollToHash('instant')) this.settleLanding();
      } else if (isFormula && this.linkDriven()) {
        // Land on the strip so the selected tab stays in view.
        this.scrollToHash(this.motion());
      }
    }

    // A formula hash lands on its tab strip, anything else on itself.
    hashLanding() {
      const id = this.hashId();
      const target = id && document.getElementById(id);
      if (!target || !this.contains(target)) return null;
      if (!id.startsWith(FORMULA_PREFIX)) return { el: target, margin: parseFloat(getComputedStyle(target).scrollMarginTop) || 0 };
      const strip = this.querySelector('[data-formula-tabs]');
      return { el: strip, margin: this.restingMargin(strip, 24), strip: true };
    }

    scrollToHash(behavior, onlyIfOff = false) {
      const landing = this.hashLanding();
      if (!landing) return false;
      const { el, margin, strip } = landing;
      if (onlyIfOff && Math.abs(el.getBoundingClientRect().top - margin) <= 1) return true;
      const run = () => {
        if (strip) window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - margin), behavior });
        else el.scrollIntoView({ block: 'start', behavior });
      };
      if (behavior === 'instant') this.scrollInstantly(run);
      else run();
      return true;
    }

    // While loading, the browser re-runs its fragment scroll as layout shifts and fonts swap: hold the landing until the visitor moves or the load settles.
    settleLanding() {
      const { signal } = this.controller;
      const release = this.lockScroll();
      this.settling = true;
      const stop = () => {
        this.settling = false;
        release();
      };
      this.stopSettling = stop;
      const realign = () => { if (this.settling) this.scrollToHash('instant', true); };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) => window.addEventListener(type, stop, { passive: true, once: true, signal }));
      window.addEventListener('scroll', realign, { passive: true, signal });
      document.fonts?.addEventListener('loadingdone', realign, { signal });
      document.fonts?.ready.then(realign);
      const close = () => setTimeout(stop, 2000);
      if (document.readyState === 'complete') close();
      else window.addEventListener('load', () => { realign(); close(); }, { once: true, signal });
      setTimeout(stop, 8000);
    }

    // Lifts html{scroll-behavior:smooth}; call the result to restore it.
    lockScroll() {
      const root = document.documentElement;
      if (!this.scrollLocks) this.savedBehavior = root.style.scrollBehavior;
      this.scrollLocks = (this.scrollLocks || 0) + 1;
      root.style.scrollBehavior = 'auto';
      let released = false;
      return () => {
        if (released) return;
        released = true;
        this.scrollLocks -= 1;
        if (!this.scrollLocks) root.style.scrollBehavior = this.savedBehavior;
      };
    }

    // A hash arrival never animates down the page.
    scrollInstantly(run) {
      const release = this.lockScroll();
      run();
      requestAnimationFrame(release);
      setTimeout(release, 250);
    }

    motion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    }

    headerReserve() {
      return parseFloat(getComputedStyle(this).getPropertyValue('--fr-header-height')) || 0;
    }

    // Resting distance from the top: the scroll-margin-top, or the header reserve plus gap when none.
    restingMargin(el, gap) {
      return parseFloat(getComputedStyle(el).scrollMarginTop) || this.headerReserve() + gap;
    }

    scrollToElement(el, gap, behavior = this.motion()) {
      window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - this.restingMargin(el, gap)), behavior });
    }

    revealPanel(name) {
      if (!matchMedia(PHONE).matches) return;
      const panel = this.panels.find((item) => item.dataset.formulaPanel === name);
      if (panel && panel.getBoundingClientRect().top > window.innerHeight * 0.6) this.scrollToElement(this.querySelector('[data-formula-tabs]'), 16);
    }

    focusLibrary() {
      const title = this.querySelector('#research-sources-title');
      if (title && !title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      (title || this.querySelector('#research-sources'))?.focus({ preventScroll: true });
    }

    // The source row a visitor just arrived on, highlighted until a tab or filter change or 4s.
    markLanded(row) {
      this.clearLanded();
      row.classList.add('is-landed');
      this.landed = row;
      this.landedTimer = setTimeout(() => this.clearLanded(), 4000);
    }

    clearLanded() {
      clearTimeout(this.landedTimer);
      this.landed?.classList.remove('is-landed');
      this.landed = null;
    }

    // --fr-header-height is the real header height only while the header can stay pinned (a sticky header in a header-tall wrapper scrolls away).
    updateHeaderReserve() {
      const header = document.querySelector('.site-header');
      this.style.setProperty('--fr-header-height', header && this.canStick(header) ? `${Math.ceil(header.getBoundingClientRect().height)}px` : '0px');
    }

    canStick(header) {
      for (let el = header; el && el !== document.body; el = el.parentElement) {
        const { position } = getComputedStyle(el);
        if (position === 'fixed') return true;
        if (position === 'sticky' && el.parentElement && el.parentElement.offsetHeight > el.offsetHeight + 1) return true;
      }
      return false;
    }

    // "[N]" citations get an accessible name with that text plus the source title.
    labelCitations() {
      this.querySelectorAll('a[data-source-link]:not([aria-label])').forEach((link) => {
        const label = link.textContent.trim();
        if (!/^\[\d+\]$/.test(label) || !link.hash) return;
        const title = this.querySelector(link.hash)?.querySelector('.fr-source__title');
        if (title) link.setAttribute('aria-label', `${label} ${visibleText(title)}`);
      });
    }

    // One searchable record per source, from the text a reader sees.
    indexSource(source) {
      const keywords = source.dataset.sourceKeywords || '';
      const parts = ['.fr-source__title', '.fr-source__author', '.fr-source__note', '.fr-source__kind'].map((selector) => {
        const node = source.querySelector(selector);
        return node ? visibleText(node) : '';
      });
      const text = `${parts.join(' ')} ${keywords}`.toLocaleLowerCase();
      return {
        source,
        text,
        words: text.match(/[a-z0-9]+/g) || [],
        keywords: keywords.split(/\s+/),
        number: parseInt(source.querySelector('.fr-source__number')?.textContent.match(/\d+/)?.[0], 10)
      };
    }

    matchesTerm(entry, term) {
      // "[17]" or a bare 1-2 digit number is a citation: match that source only.
      const cited = /^\[(\d{1,2})\]$|^(\d{1,2})$/.exec(term);
      if (cited) {
        const number = Number(cited[1] ?? cited[2]);
        if (this.numbers.has(number)) return entry.number === number;
        if (cited[1] !== undefined) return false;
      }
      return term.length === 1 ? entry.words.some((word) => word.startsWith(term)) : entry.text.includes(term);
    }

    resetSourceFilter() {
      if (!this.search.value && this.sourceFormula === 'all') return;
      this.search.value = '';
      this.sourceFormula = 'all';
      this.shortcutFormula = false;
      this.filterSources();
    }

    filterSources(typing = false) {
      const terms = this.search.value.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      let count = 0;
      this.index.forEach((entry) => {
        const formulaMatches = this.sourceFormula === 'all' || entry.keywords.includes('all') || entry.keywords.includes(this.sourceFormula);
        entry.source.hidden = !formulaMatches || !terms.every((term) => this.matchesTerm(entry, term));
        if (!entry.source.hidden) count += 1;
      });
      this.clearLanded();
      this.sourceFilters.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.sourceFilter === this.sourceFormula)));
      this.clearButton.hidden = !this.search.value && this.sourceFormula === 'all';
      const formulaLabel = this.sourceFormula === 'all' ? '' : ` for ${this.sourceFormula[0].toUpperCase()}${this.sourceFormula.slice(1)}`;
      this.announceCount(`${count} of ${this.sources.length} sources${formulaLabel}`, typing);
      this.querySelector('[data-source-empty]').hidden = count !== 0;
      this.queueReadingPosition();
    }

    // The count is a polite live region: written after a typing pause, never rewritten when unchanged.
    announceCount(text, typing) {
      clearTimeout(this.countTimer);
      const write = () => { if (this.countNode.textContent !== text) this.countNode.textContent = text; };
      if (typing) this.countTimer = setTimeout(write, 350);
      else write();
    }

    queueReadingPosition() {
      if (this.readingFrame) return;
      this.readingFrame = requestAnimationFrame(() => {
        this.readingFrame = null;
        const readingLine = this.headerReserve() + 48;
        let current = -1;
        this.readingSections.forEach((section, index) => {
          if (section.getBoundingClientRect().top <= readingLine) current = index;
        });
        this.jumpLinks.forEach((link, index) => {
          if (index === current) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.headerObserver?.disconnect();
      cancelAnimationFrame(this.readingFrame);
      this.readingFrame = null;
      clearTimeout(this.countTimer);
      this.clearLanded();
      this.stopSettling?.();
    }
  });
}
